import types from '../mutation-types';
import FollowUpsAPI from '../../api/followUps';
import { emitter } from 'shared/helpers/mitt';

export const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

export const getters = {
  getFollowUps: _state => conversationId => {
    const records = _state.records[conversationId] || [];
    return [...records].sort((r1, r2) => r1.scheduled_at - r2.scheduled_at);
  },
  getPendingFollowUps: (_state, _getters) => conversationId => {
    return _getters
      .getFollowUps(conversationId)
      .filter(record => record.status === 'pending');
  },
  getFollowUpById: _state => (conversationId, id) => {
    const records = _state.records[conversationId] || [];
    return records.find(record => record.id === id);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

// Keeps the conversation's `has_pending_follow_up` flag (used by the Follow-ups
// tab list filter) in sync with the follow-up records after a change, so the
// conversation appears/disappears without needing a page refresh.
const syncConversationFollowUpFlag = (
  { commit, getters, rootGetters },
  conversationId
) => {
  const conversation = rootGetters.getConversationById(conversationId);
  if (!conversation) return;
  commit(
    'UPDATE_CONVERSATION',
    {
      id: conversation.id,
      updated_at: conversation.updated_at,
      has_pending_follow_up:
        getters.getPendingFollowUps(conversationId).length > 0,
    },
    { root: true }
  );
};

export const actions = {
  async get({ commit }, { conversationId }) {
    commit(types.SET_FOLLOW_UPS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await FollowUpsAPI.get(conversationId);
      commit(types.SET_FOLLOW_UPS, { conversationId, data });
    } finally {
      commit(types.SET_FOLLOW_UPS_UI_FLAG, { isFetching: false });
    }
  },

  async create(context, { conversationId, description, scheduledAt }) {
    const { commit } = context;
    commit(types.SET_FOLLOW_UPS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await FollowUpsAPI.create(conversationId, {
        description,
        scheduledAt,
      });
      commit(types.ADD_FOLLOW_UP, { conversationId, data });
      syncConversationFollowUpFlag(context, conversationId);
      emitter.emit('fetch_conversation_stats');
      return data;
    } finally {
      commit(types.SET_FOLLOW_UPS_UI_FLAG, { isCreating: false });
    }
  },

  async complete(context, { conversationId, id }) {
    const { commit } = context;
    commit(types.SET_FOLLOW_UPS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await FollowUpsAPI.complete(conversationId, id);
      commit(types.UPDATE_FOLLOW_UP, { conversationId, data });
      syncConversationFollowUpFlag(context, conversationId);
      emitter.emit('fetch_conversation_stats');
    } finally {
      commit(types.SET_FOLLOW_UPS_UI_FLAG, { isUpdating: false });
    }
  },

  async cancel(context, { conversationId, id }) {
    const { commit } = context;
    commit(types.SET_FOLLOW_UPS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await FollowUpsAPI.cancel(conversationId, id);
      commit(types.UPDATE_FOLLOW_UP, { conversationId, data });
      syncConversationFollowUpFlag(context, conversationId);
      emitter.emit('fetch_conversation_stats');
    } finally {
      commit(types.SET_FOLLOW_UPS_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_FOLLOW_UPS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },

  [types.SET_FOLLOW_UPS]($state, { conversationId, data }) {
    $state.records = { ...$state.records, [conversationId]: data };
  },

  [types.ADD_FOLLOW_UP]($state, { conversationId, data }) {
    const records = $state.records[conversationId] || [];
    $state.records = {
      ...$state.records,
      [conversationId]: [...records, data],
    };
  },

  [types.UPDATE_FOLLOW_UP]($state, { conversationId, data }) {
    const records = $state.records[conversationId] || [];
    $state.records = {
      ...$state.records,
      [conversationId]: records.map(record =>
        record.id === data.id ? data : record
      ),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
